/**
 * Domain vocabulary for MindPeer Auth.
 *
 * These constants are the single source of truth for status/role/source values
 * that appear in MongoDB documents, JWT claims and API payloads. They mirror
 * the PRD sections noted on each block.
 */

/** PRD §14 `users.status` */
export const USER_STATUS = ['active', 'pending', 'suspended', 'disabled'] as const;
export type UserStatus = (typeof USER_STATUS)[number];

/** PRD §14 `organizations.type`. `global` is the reserved MindPeer Global org. */
export const ORGANIZATION_TYPE = ['business', 'global'] as const;
export type OrganizationType = (typeof ORGANIZATION_TYPE)[number];

/** PRD §14 `organizations.status` */
export const ORGANIZATION_STATUS = ['active', 'suspended', 'disabled'] as const;
export type OrganizationStatus = (typeof ORGANIZATION_STATUS)[number];

/**
 * PRD §7 — how a user may become a member of an organization.
 * `verified-domain` requires a verified + autoJoin domain record.
 * `invitation-only` means domain matching never grants membership.
 * `closed` means only a platform admin may add members.
 */
export const JOIN_POLICY = ['verified-domain', 'invitation-only', 'closed'] as const;
export type JoinPolicy = (typeof JOIN_POLICY)[number];

/** PRD §14 — provenance of a membership record. */
export const MEMBERSHIP_SOURCE = [
  'verified-domain',
  'invitation',
  'platform-admin',
  'global-email',
  'migration',
] as const;
export type MembershipSource = (typeof MEMBERSHIP_SOURCE)[number];

/** PRD §14 `memberships.status` */
export const MEMBERSHIP_STATUS = ['active', 'pending', 'suspended', 'revoked'] as const;
export type MembershipStatus = (typeof MEMBERSHIP_STATUS)[number];

/** PRD §13 — platform-wide roles, independent of any organization. */
export const PLATFORM_ROLE = ['platform_admin', 'support_admin'] as const;
export type PlatformRole = (typeof PLATFORM_ROLE)[number];

/** PRD §13 — roles scoped to a single organization membership. */
export const ORGANIZATION_ROLE = ['owner', 'admin', 'member', 'viewer'] as const;
export type OrganizationRole = (typeof ORGANIZATION_ROLE)[number];

/** PRD §8.1 — entitlement lifecycle. Only `active` and `trial` grant access. */
export const ENTITLEMENT_STATUS = ['active', 'trial', 'suspended', 'expired', 'disabled'] as const;
export type EntitlementStatus = (typeof ENTITLEMENT_STATUS)[number];

/** Entitlement statuses that may permit module entry, subject to date checks. */
export const GRANTING_ENTITLEMENT_STATUSES: readonly EntitlementStatus[] = ['active', 'trial'];

/** PRD §9 `applications.status` */
export const APPLICATION_STATUS = ['active', 'suspended', 'disabled'] as const;
export type ApplicationStatus = (typeof APPLICATION_STATUS)[number];

/** PRD §9 — public browser clients must use PKCE; confidential clients hold a secret. */
export const APPLICATION_TYPE = ['public', 'confidential'] as const;
export type ApplicationType = (typeof APPLICATION_TYPE)[number];

/**
 * Machine-readable reasons an authorization request can be refused (PRD §8.3).
 * The portal maps these to user-facing copy; modules never see the raw reason.
 */
export const ACCESS_DENIAL_REASON = [
  'user_suspended',
  'email_unverified',
  'organization_inactive',
  'membership_inactive',
  'no_membership',
  'awaiting_organization_approval',
  'application_unknown',
  'application_inactive',
  'entitlement_missing',
  'entitlement_expired',
  'entitlement_suspended',
  'entitlement_not_started',
  'global_org_not_permitted',
  'seat_limit_reached',
] as const;
export type AccessDenialReason = (typeof ACCESS_DENIAL_REASON)[number];

/** PRD §17 — audit event types. */
export const AUDIT_EVENT = [
  'login.success',
  'login.failure',
  'login.locked_out',
  'signup',
  'email.verification_sent',
  'email.verified',
  'password.reset_requested',
  'password.reset_completed',
  'password.changed',
  'organization.assigned',
  'organization.switched',
  'invitation.created',
  'invitation.accepted',
  'entitlement.changed',
  'user.suspended',
  'user.reinstated',
  'session.created',
  'session.revoked',
  'logout',
  'user.linked_identity',
  'authorization.granted',
  'authorization.denied',
  'token.issued',
  'token.refreshed',
  'token.refresh_reuse_detected',
  'admin.change',
] as const;
export type AuditEvent = (typeof AUDIT_EVENT)[number];

export const AUDIT_RESULT = ['success', 'failure', 'denied'] as const;
export type AuditResult = (typeof AUDIT_RESULT)[number];

/**
 * Identity providers this service will accept a federated sign-in from.
 *
 * The provider plus the subject it issued is what a `users.federatedIdentities`
 * entry records. It is a list rather than a single `google` literal because the
 * second one (Microsoft, an enterprise OIDC connector) changes only this array
 * and the service that builds the authorization URL — everything downstream,
 * including linking and account creation, is already provider-agnostic.
 */
export const FEDERATED_PROVIDER = ['google'] as const;
export type FederatedProvider = (typeof FEDERATED_PROVIDER)[number];

/** Reserved slug for the MindPeer Global organization (PRD §7.3). */
export const GLOBAL_ORG_SLUG = 'global';

/**
 * Generic/consumer email providers whose users are assigned to the Global
 * organization (PRD §7.3). Overridable at runtime via GENERIC_EMAIL_DOMAINS so
 * ops can extend the list without a deploy.
 */
export const DEFAULT_GENERIC_EMAIL_DOMAINS = [
  'gmail.com',
  'googlemail.com',
  'outlook.com',
  'hotmail.com',
  'live.com',
  'msn.com',
  'yahoo.com',
  'ymail.com',
  'proton.me',
  'protonmail.com',
  'pm.me',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'gmx.com',
  'gmx.net',
  'mail.com',
  'zoho.com',
  'yandex.com',
  'fastmail.com',
  'tutanota.com',
  'hey.com',
] as const;
